/**
 * Ride Search Bar Component
 * 
 * Reusable search and filter component for rides.
 * Provides debounced search input and filter options for date range,
 * passenger type, and trip type.
 * 
 * @module RideSearchBar
 */

import React from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
/**
 * Filter options for rides
 */
export interface RideFilterOptions {
  /** Search query string */
  searchQuery: string;
  /** Start date for date range filter (YYYY-MM-DD) */
  startDate?: string;
  /** End date for date range filter (YYYY-MM-DD) */
  endDate?: string;
  /** Filter by passenger type */
  passengerType?: 'standard' | 'wheelchair' | 'all';
  /** Filter by trip type */
  tripType?: 'leaveAt' | 'arriveBy' | 'all';
}

/**
 * Props for RideSearchBar component
 */
export interface RideSearchBarProps {
  /** Current search query */
  searchQuery: string;
  /** Callback when search query changes */
  onSearchChange: (query: string) => void;
  /** Current filter options */
  filters?: RideFilterOptions;
  /** Callback when filters change */
  onFiltersChange?: (filters: RideFilterOptions) => void;
  /** Number of filtered results (for display) */
  filteredCount?: number;
  /** Total number of results (for display) */
  totalCount?: number;
  /** Placeholder text for search input */
  placeholder?: string;
}

/**
 * RideSearchBar Component
 * 
 * Provides search and filtering functionality for rides.
 */
export const RideSearchBar: React.FC<RideSearchBarProps> = ({
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  filteredCount,
  totalCount,
  placeholder,
}) => {
  const { t } = useTranslation('mapTransfer');
  const resolvedPlaceholder = placeholder ?? t('rides.searchBar.defaultPlaceholder');
  const handleClearSearch = () => {
    onSearchChange('');
  };

  const handleFilterChange = (key: keyof RideFilterOptions, value: any) => {
    if (onFiltersChange && filters) {
      onFiltersChange({
        ...filters,
        [key]: value,
      });
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={resolvedPlaceholder}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon name="magnifying-glass" size={20} />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClearSearch}
                edge="end"
              >
                <Icon name="xmark" size={20} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      {/* Filter Options */}
      {onFiltersChange && filters && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('rides.searchBar.passengerType')}</InputLabel>
            <Select
              value={filters.passengerType || 'all'}
              label={t('rides.searchBar.passengerType')}
              onChange={(e) => handleFilterChange('passengerType', e.target.value)}
            >
              <MenuItem value="all">{t('rides.searchBar.all')}</MenuItem>
              <MenuItem value="standard">{t('rides.searchBar.standard')}</MenuItem>
              <MenuItem value="wheelchair">{t('rides.searchBar.wheelchair')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('rides.searchBar.tripType')}</InputLabel>
            <Select
              value={filters.tripType || 'all'}
              label={t('rides.searchBar.tripType')}
              onChange={(e) => handleFilterChange('tripType', e.target.value)}
            >
              <MenuItem value="all">{t('rides.searchBar.all')}</MenuItem>
              <MenuItem value="leaveAt">{t('rides.searchBar.leaveAt')}</MenuItem>
              <MenuItem value="arriveBy">{t('rides.searchBar.arriveBy')}</MenuItem>
            </Select>
          </FormControl>

          {filteredCount !== undefined && totalCount !== undefined && (
            <Chip
              label={t('rides.searchBar.countChip', { filtered: filteredCount, total: totalCount })}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )}
    </Box>
  );
};

